import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import { LinearProgress } from '@/UI/LinearProgress/LinearProgress';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { Input } from '@/UI/Input/Input';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { log } from '@/logger/logger.ts';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { useIsHovered } from '@/hooks/useIsHovered';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import {
  AppDialog,
  AppDialogContent,
  AppDialogSection,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogActions,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { FieldLabel, useLabelFieldOffset } from '@/UI/FieldLabel/FieldLabel';
import { Flex, FlexCenHorVer, FlexCenVer, FlexCol } from '@/UI/styles';
import { handleFileInputChange } from '@/utils/fileUploadUtils';
import { uploadLoraUsingPreSignedUrl, updateLoraMetadata } from '@/utils/loraUploadUtils';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { ImageIcon, UploadIcon } from '@/UI/Icons';
import { useResolvedAssets } from '@/state/workflow.state';
import { assetThumbnailUrl } from '@/utils/assetUrls.ts';
import { RunType } from '@/enums/run-type.enum';
import { uploadFile } from '../../Nodes/Utils';
import { coverImageFromAsset } from './loraCoverImage';
import type { MediaAsset } from '@/types/api/assets';
import type { ImageFile, Lora } from 'web';

const logger = log.getLogger('LoRAModal');

interface LoRAModalProps {
  open: boolean;
  onClose: () => void;
  lora: Lora;
  updateLora: (lora: Lora) => void;
}

function LoRAModal({ open, onClose, lora, updateLora }: LoRAModalProps) {
  const { t: translate } = useTranslation();
  const isFpl = useFplMigrationFlag();
  const labelFieldOffset = useLabelFieldOffset();
  const uploadBorderColor = isFpl ? color.White04_T : color.White08_T;
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [_uploadImageProgress, setUploadImageProgress] = useState(0);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [_imageUploadError, setImageUploadError] = useState(false);
  const { isHovered: imageHover, ...imageHoverProps } = useIsHovered();
  const [previewImage, setPreviewImage] = useState(
    assetThumbnailUrl(lora.coverImage, useResolvedAssets(), { type: 'image' }) ?? '',
  );
  const [isClosing, setIsClosing] = useState(false);

  const [localName, setLocalName] = useState(String(lora.name));
  const [localTrigger, setLocalTrigger] = useState(lora.trigger);
  const [localImage, setLocalImage] = useState<ImageFile | undefined>(lora.coverImage);
  const [localFile, setLocalFile] = useState(lora.file || '');
  const [localDefaultWeight, setLocalDefaultWeight] = useState(lora.defaultWeight || 0.5);

  const extension = '.safetensors';
  const filename =
    localFile?.indexOf(extension) > 0
      ? localFile.substring(0, localFile.indexOf(extension) + extension.length)
      : localFile;

  const hasLoraData = Boolean(lora.file);
  const modalTitle = hasLoraData
    ? translate(I18N_KEYS.UPLOAD_LORA_NODE.EDIT_TITLE)
    : translate(I18N_KEYS.UPLOAD_LORA_NODE.UPLOAD_TITLE);

  const handleNameChange = (value: string) => {
    setLocalName(String(value));
  };

  const handleTriggerChange = (value: string) => {
    setLocalTrigger(value);
  };

  const handleDefaultWeightChange = (value: string) => {
    // A number `<Input>` surfaces its value as a string; legacy state stored it as-is.
    // @ts-expect-error -- localDefaultWeight is number-typed but holds the raw string at runtime
    setLocalDefaultWeight(value);
  };

  const uploadFileToS3 = async (file: File) => {
    setIsUploadingFile(true);
    setErrorMessage('');
    try {
      const loraId = await uploadLoraUsingPreSignedUrl(file, (progress) => setUploadProgress(progress));

      setLocalFile(loraId);
      setUploadProgress(100);
      setTimeout(() => {
        setIsUploadingFile(false);
      }, 100);
    } catch (error) {
      logger.error('Failed uploading Lora', error);
      setErrorMessage('File upload failed. Please try again.');
      setIsUploadingFile(false);
      setUploadProgress(0);
    }
  };

  const handleClickUploadImage = () => {
    const inputElement = document.getElementById(`image-input-${lora.id}`);
    if (inputElement) {
      inputElement.click();
    } else {
      logger.error('Image input element not found');
    }
  };

  const uploadImageSuccess = (file: MediaAsset) => {
    setLocalImage(coverImageFromAsset(file));
    setIsUploadingImage(false);
  };

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    handleFileInputChange(event, (file) => {
      // Preview image doesn't work for heic files
      if (!file.name.toLowerCase().includes('.heic')) {
        setPreviewImage(URL.createObjectURL(file));
      }
      setIsUploadingImage(true);
      void uploadFile([file], setUploadImageProgress, uploadImageSuccess, setImageUploadError, RunType.WorkflowEditor);
    });
  };

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    handleFileInputChange(event, (file) => {
      void uploadFileToS3(file);
    });
  };

  const handleClickUploadFile = () => {
    const inputElement = document.getElementById(`file-input-${lora.id}`);
    if (inputElement) {
      inputElement.click();
    }
  };

  const handleClose = async () => {
    if (localFile && localTrigger !== lora.trigger) {
      setIsClosing(true);
      try {
        await updateLoraMetadata(localFile, localTrigger);
      } catch (error) {
        logger.error('Error updating lora metadata', error);
        setErrorMessage('Failed to update file trigger word.');
      } finally {
        setIsClosing(false);
      }
    }

    // Update local state
    updateLora({
      ...lora,
      name: localName,
      trigger: localTrigger,
      coverImage: localImage,
      file: localFile,
      defaultWeight: localDefaultWeight,
    });

    onClose();
  };

  return (
    <AppDialog
      id="lora-modal"
      open={open}
      onClose={() => void handleClose()}
      aria-labelledby="lora-modal"
      width="lg"
      slotProps={{
        paper: {
          sx: {
            background: token.bg,
          },
        },
      }}
    >
      <AppDialogSection>
        <AppDialogHeader component="div" sx={{ p: 0, pt: 5.25, px: 3 }}>
          <AppDialogTitle>{modalTitle}</AppDialogTitle>
          <AppDialogXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={() => void handleClose()} />
        </AppDialogHeader>

        <AppDialogContent sx={{ px: 3, mt: 3, pb: 0 }}>
          <Flex id="lora-modal-content-container" sx={{ gap: 2, alignItems: 'stretch' }}>
            <FlexCol
              id="modal-lora-image-container"
              sx={{ width: 160, flexShrink: 0, position: 'relative', minHeight: 0 }}
            >
              <FieldLabel htmlFor={`image-input-${lora.id}`}>{translate(I18N_KEYS.GENERAL.IMAGE)}</FieldLabel>
              <FlexCenHorVer
                sx={{
                  width: '100%',
                  mt: labelFieldOffset,
                  flex: 1,
                  minHeight: 0,
                  borderRadius: '4px',
                  backgroundColor: isFpl ? cssVars.color.bgSecondary : 'transparent',
                  border: previewImage ? `1px solid transparent` : `1px dashed ${uploadBorderColor}`,
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onClick={() => handleClickUploadImage()}
                {...imageHoverProps}
              >
                {previewImage && (
                  <>
                    <img
                      src={previewImage}
                      alt={lora.name}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        filter: imageHover ? 'brightness(0.6)' : 'brightness(1)',
                      }}
                    />
                    {!isUploadingImage && (
                      <UploadIcon
                        style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                      />
                    )}
                    {isUploadingImage && (
                      <Box sx={{ position: 'absolute' }}>
                        <CircularProgress size={20} color="weavy_cta_secondary" />
                      </Box>
                    )}
                  </>
                )}
                {!previewImage && (
                  <>
                    <UploadIcon
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        opacity: imageHover ? 1 : 0,
                        transition: 'opacity 0.1s ease-in-out',
                      }}
                    />
                    <ImageIcon
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        opacity: imageHover ? 0 : 1,
                        transition: 'opacity 0.1s ease-in-out',
                      }}
                    />
                  </>
                )}
              </FlexCenHorVer>
              <input
                id={`image-input-${lora.id}`}
                type="file"
                onChange={handleImageUpload}
                accept=".jpeg,.jpg,.png,.webp,.heic"
                style={{ display: 'none' }}
              />
            </FlexCol>
            <FlexCol id="lora-modal-name-trigger-file" sx={{ gap: 2, flex: 1, minWidth: 0 }}>
              <Input
                fullWidth
                label={translate(I18N_KEYS.GENERAL.NAME)}
                value={localName}
                onChange={(event) => handleNameChange(event.target.value)}
                placeholder={localName}
                autoComplete="off"
              />
              <Flex id="lora-modal-trigger-weight-container" sx={{ width: '100%', gap: 1 }}>
                <Box sx={{ width: '50%', minWidth: 0 }}>
                  <Input
                    fullWidth
                    label={translate(I18N_KEYS.UPLOAD_LORA_NODE.TRIGGER)}
                    value={localTrigger}
                    onChange={(event) => handleTriggerChange(event.target.value)}
                    placeholder={localTrigger}
                    autoComplete="off"
                  />
                </Box>
                <Box sx={{ width: '50%', minWidth: 0 }}>
                  <Input
                    type="number"
                    fullWidth
                    label={translate(I18N_KEYS.UPLOAD_LORA_NODE.DEFAULT_WEIGHT)}
                    value={localDefaultWeight}
                    onChange={(event) => handleDefaultWeightChange(event.target.value)}
                    placeholder={String(localDefaultWeight)}
                    autoComplete="off"
                    inputProps={{
                      step: 0.1,
                      min: 0,
                    }}
                  />
                </Box>
              </Flex>
              <FlexCol id="lora-upload-container" sx={{ position: 'relative' }}>
                <FieldLabel htmlFor={`file-input-${lora.id}`}>{translate(I18N_KEYS.GENERAL.FILE)}</FieldLabel>
                <Box sx={{ mt: labelFieldOffset }}>
                  {localFile !== '' && (
                    <TextSmRg sx={{ color: token.textSecondary, display: 'block' }}>
                      {filename.length > 30 ? filename.substring(0, 27) + '...' : filename}
                      <span style={{ marginLeft: 8 }}>
                        <ButtonTextPlain variant="body-sm-rg" onClick={() => handleClickUploadFile()}>
                          {translate(I18N_KEYS.UPLOAD_LORA_NODE.UPLOAD_ANOTHER_BUTTON_TEXT_FILE)}
                        </ButtonTextPlain>
                      </span>
                    </TextSmRg>
                  )}
                  {!isUploadingFile && localFile === '' && (
                    <label
                      htmlFor={`file-input-${lora.id}`}
                      style={{
                        cursor: 'pointer',
                        height: '32px',
                        width: '100%',
                        borderRadius: 4,
                        border: `1px dashed ${uploadBorderColor}`,
                        backgroundColor: isFpl ? cssVars.color.bgSecondary : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <TextSmRg>{translate(I18N_KEYS.UPLOAD_LORA_NODE.UPLOAD_BUTTON_TEXT)}</TextSmRg>
                    </label>
                  )}
                  <input
                    id={`file-input-${lora.id}`}
                    type="file"
                    onChange={handleFileUpload}
                    accept=".safetensors"
                    style={{ display: 'none' }}
                  />
                  {isUploadingFile && (
                    <Box sx={{ width: '100%' }}>
                      <TextSmRg sx={{ color: token.textSecondary, fontStyle: 'italic' }}>
                        {translate(I18N_KEYS.UPLOAD_LORA_NODE.UPLOADING)}
                      </TextSmRg>
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          color="weavy_cta_secondary"
                          value={uploadProgress}
                          sx={{ height: '2px' }}
                          fplProps={{
                            height: 2,
                            'aria-label': translate(I18N_KEYS.COMMON_COMPONENTS.PROGRESS.UPLOAD_ARIA),
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                  {errorMessage && (
                    <TextSmRg color="error" sx={{ mt: 1 }}>
                      {errorMessage}
                    </TextSmRg>
                  )}
                </Box>
              </FlexCol>
            </FlexCol>
          </Flex>
        </AppDialogContent>

        <AppDialogActions sx={{ px: 3, pt: 4, pb: 3, justifyContent: 'flex-end' }}>
          <ButtonContained size="small" mode="filled-light" onClick={() => void handleClose()} disabled={isClosing}>
            {isClosing ? (
              <FlexCenVer sx={{ gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                {translate(I18N_KEYS.GENERAL.SAVING)}
              </FlexCenVer>
            ) : (
              translate(I18N_KEYS.GENERAL.DONE)
            )}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
}

export default LoRAModal;
